import { MessageBoxButton, router, userInteraction } from "@eusoft/webapp-framework";
import { type IFeature, type IContent, formatText } from "@eusoft/webapp-ui";
import { SessionValidation, userSession } from "./UserSession";
import { navigatePage } from "../actions/Common";
import { UserAccountType } from "../entities/Commands";
import { EMPTY_GUID, type ResourceName, type ResourceScope } from "../entities/Types";
import type { IAuthorizeFeature, IResourcePermission } from "../abstraction/IAuthorizeFeature";


interface IAuthorizeOptions {
    accountType?: UserAccountType;
    resources?: IResourcePermission[];
    allowTemporary?: boolean;
    allowAnonymous?: boolean;
}

class AuthorizeBuilder<TArgs> {
    constructor(args: TArgs) {
        this.args = args;
    }

    resource(res: ResourceName, scope: ResourceScope | ResourceScope[], resId: string = undefined) {
        this.options.resources ??= [];  
        this.options.resources.push({ res, scope, resId }); 
        return this;
    }

    businessAccount() {
        this.options.accountType = UserAccountType.Business;    
        return this;
    }

    personalAccount() {
        this.options.accountType = UserAccountType.Business;
        return this;
    }

    allowTemporary() {
        this.options.allowTemporary = true;
        return this;
    }

    allowAnonymous() {
        this.options.allowAnonymous = true;
        return this;
    }

    readonly args: TArgs;

    readonly options = {} as IAuthorizeOptions;
}


export function Authorize< 
    TArgs extends ObjectLike,
    TContent extends IContent<TArgs> = IContent<TArgs>>(options?: IAuthorizeOptions | { (bld: AuthorizeBuilder<TArgs>): unknown }): IFeature<TContent> {

    const result = async (page: TContent, args: TArgs) => {

        if (typeof options === "function") {
            const bld = new AuthorizeBuilder(args);
            options(bld);
            options = bld.options;
        }

        const valid = await userSession.validateAsync();

        if (valid == SessionValidation.Invalid) {

            if (userSession.active) {

                userSession.invalidate();

                await userInteraction.messageBoxAsync(formatText("msg-session-closed"), "attention", MessageBoxButton.Ok);
            }

            if (options?.allowAnonymous)
                return true;

            navigatePage("login", { action: () => router.navigatePageAsync(page, args, "replace") }, "replace");

            return false;
        }



        if (!options?.allowTemporary && userSession.active.user.id == EMPTY_GUID) {

            navigatePage("login", { action: () => router.navigatePageAsync(page, args, "replace") }, "replace");

            return false;
        }
            
        if (!await userSession.isDeviceValidAsync()) {

            navigatePage("register-device", { action: () => router.navigatePageAsync(page, args, "replace") }, "replace");

            return false;
        }

        if (options?.accountType !== undefined && options?.accountType != userSession.active?.user?.accountType) {

            navigatePage("error", { message: "msg-not-authorized" }, "replace");

            return false;
        }

        if (options?.resources && !hasPermission(options.resources)) {

            userInteraction.messageAsync(formatText("err-not-authorized"), "error");
            return false;
        }

        return true;
    }

    let staticOptions = options;

    if (typeof staticOptions === "function") {
        const bld = new AuthorizeBuilder({} as TArgs);
        staticOptions(bld);
        staticOptions = bld.options;
    }

    result.type = "Authorize";
    result.data = staticOptions?.resources;

    return result;
}

export function hasPermission(permissions: IResourcePermission[]) {

    return permissions.every(a => {
        if (Array.isArray(a.scope))
            return a.scope.some(s => userSession.hasPermission(a.res, s));
        return userSession.hasPermission(a.res, a.scope);
    })
}